import { useDocumentDownloaderProps } from "@fero/hooks";
import { PortalPageContent, PortalPageContentHeader } from "@fero/layouts";
import { Stack, Tab, Tabs, Typography } from "@mui/material";
import type {
  ProgressRevaluationRequestResp,
  RevaluationDetailsResp,
} from "adl-gen/ferovinum/app/api";
import type { AttachedDocument } from "adl-gen/ferovinum/app/uploads";
import { RevaluationStepper } from "components//widgets/revaluation-requests/revaluation-stepper";
import { AcceptTermsComponent } from "components/library/widgets/accept-terms-component";
import { RevaluationPricingTotalsTable } from "components/library/widgets/revaluation-pricing-totals-table";
import { DocumentDownloader } from "components/widgets/buttons/document-downloader";
import { LabelledCard } from "components/widgets/labelled-card/labelled-card";
import { TermsLink } from "components/widgets/link/terms-link";
import { useState } from "react";

type RevaluationRequestSections = "progress" | "summary" | "contractNote";

export interface RevaluationRequestDetailsPageViewProps {
  revaluationDetails: RevaluationDetailsResp;
  acknowledgeDealTerms: (
    accept: boolean,
  ) => Promise<ProgressRevaluationRequestResp | undefined>;
  attachedDocuments: AttachedDocument[];
  referenceNumber: string;
}

export const OrgRevaluationRequestDetailsPageView = ({
  revaluationDetails,
  acknowledgeDealTerms,
  attachedDocuments,
  referenceNumber,
}: RevaluationRequestDetailsPageViewProps) => {
  const [selectedSection, setSelectedSection] =
    useState<RevaluationRequestSections>("progress");
  const currentState =
    revaluationDetails.stateEvents[revaluationDetails.stateEvents.length - 1]
      .state;
  const pdfDownloaderProps = useDocumentDownloaderProps(attachedDocuments);

  return (
    <PortalPageContent
      header={
        <PortalPageContentHeader
          variant="split"
          title={"Stock Revaluation"}
          // subtitles={[{ text: `requested on ${formatLocalDate(revaluationDetails.dateOfRequest)}` }]}
          right={
            <Stack width={"100%"} textAlign={"right"}>
              <Typography sx={{ fontSize: 12, color: "common.grey5" }}>
                Reference Number
              </Typography>
              <Typography variant="subtitle1Bold">{referenceNumber}</Typography>
            </Stack>
          }
        />
      }
    >
      <Stack spacing={4}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Tabs
            value={selectedSection}
            onChange={(_, v) => setSelectedSection(v)}
          >
            <Tab label="Progress" value="progress" />
            <Tab label="Contract Note" value="contractNote" />
          </Tabs>

          <Stack
            direction="row"
            justifyContent={"space-between"}
            spacing={2}
            marginTop={2}
          >
            <DocumentDownloader {...pdfDownloaderProps} />
          </Stack>
        </Stack>
      </Stack>

      <Stack spacing={6} marginTop={5}>
        {selectedSection === "progress" && (
          <>
            {currentState === "created" && (
              <LabelledCard label="Next Action">
                <Stack spacing={6}>
                  <Typography>
                    Contract note for the revaluation for{" "}
                    {revaluationDetails.orgInfo.businessName}
                  </Typography>
                  <RevaluationPricingTotalsTable
                    revaluationDetails={revaluationDetails}
                  />
                </Stack>
                <Stack direction="row" justifyContent={"flex-end"} margin={3}>
                  <TermsLink
                    dealTermsView={revaluationDetails.dealTermsView}
                    clickText={"Deal Terms"}
                  />
                </Stack>
                <AcceptTermsComponent
                  currentState={currentState}
                  dealTerms={revaluationDetails.dealTermsView}
                  acknowledgeDealTerms={acknowledgeDealTerms}
                />
                <Stack
                  direction="row"
                  justifyContent={"flex-end"}
                  spacing={2}
                  marginTop={3}
                />
              </LabelledCard>
            )}
            <LabelledCard label="Progress">
              <RevaluationStepper
                stateEvents={revaluationDetails.stateEvents}
              />
            </LabelledCard>
          </>
        )}
        {selectedSection === "contractNote" && (
          <>
            <Stack spacing={6}>
              <RevaluationPricingTotalsTable
                revaluationDetails={revaluationDetails}
              />
            </Stack>
            <Stack
              direction="row"
              justifyContent={"space-between"}
              marginTop={5}
              marginLeft={3}
            >
              <TermsLink dealTermsView={revaluationDetails.dealTermsView} />
            </Stack>
            <Stack
              direction="row"
              justifyContent={"flex-end"}
              spacing={2}
              marginTop={3}
            />
          </>
        )}
      </Stack>
    </PortalPageContent>
  );
};
